import './add-device-modal-container.component.scss';

import { memo, useState, useEffect, useCallback, useReducer } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { cloneDeep } from 'lodash';

// Const
import { userType } from 'const/nuclias/user';

// Hook
import useMessage from 'hooks/useMessage';
import useUser from 'hooks/useUser';
import usePageContext from 'hooks/usePageContext';

// Slice
import { uiActions, selectShowModalSpinner } from 'stores/slice/ui';
import { loadGuiBootstarp, loadOrgBootstarp } from "stores/slice/fetchStatus";


// UI
import SpinnerForModal from 'ui/SpinnerForModal';

// Component
import MessageBoxGroup from 'components/MessageBoxGroup';
import ModalContainer from 'components/ModalContainer';
import TooltipDialog from 'components/TooltipDialog';
import DropdownWithItem from 'components/DropdownWithItem';
import ButtonWithIcon from 'components/ButtonWithIcon';
import Input from 'components/Input';
import Button from 'components/Button';
import Icon from 'components/Icon';

// Model
import AddDeviceModel from './model';

// Store
import addDeviceStore from './store';

// Controller
import controller from './controller';

const AddDeviceModal = (props) => {
  const {
    modalStatus,
    changeModalStatus
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Slice
  const showModalSpinner = useSelector(selectShowModalSpinner);

  // State
  const [hasUnusedActivedLicense, setHasUnusedActivedLicense] = useState(false);
  const [addDeviceState, addDeviceDispatch] = useReducer(
    addDeviceStore.reducer,
    addDeviceStore.defaultState
  );

  // Hook
  const { messages, messagesType, resetMessages, setErrorMessage } = useMessage();
  const { user } = useUser();
  const {
    selectedOrg,
    getUnusedDeviceListByUserWithManagedOrg,
    getProfileListByUserWithManagedOrg,
    getSiteListByUserWithManagedOrg
  } = usePageContext();

  // Variable
  const { type: userIdentity } = user;

  // Method
  const init = useCallback(() => {
    dispatch(uiActions.showModalSpinner());
    resetMessages();

    addDeviceDispatch({
      type: addDeviceStore.action.initializeState,
      payload: AddDeviceModel.getAddDeviceState(
        getUnusedDeviceListByUserWithManagedOrg(),
        getProfileListByUserWithManagedOrg(),
        getSiteListByUserWithManagedOrg()
      )
    });

    setHasUnusedActivedLicense(false);
    setTimeout(() => {
      dispatch(uiActions.hideModalSpinner());
    }, 1000);
  }, [selectedOrg.deviceGroupObj, selectedOrg.deviceObj, selectedOrg.storeObj]);

  const resetForm = () => {
    resetMessages();
    addDeviceDispatch({ type: addDeviceStore.action.resetState });
  }

  const changeDeviceUid = (value) => {
    const updatedDeviceUid = cloneDeep(addDeviceState.deviceUid);
    AddDeviceModel.changeDeviceUid(updatedDeviceUid, value, updatedDeviceUid.originList);

    addDeviceDispatch({
      type: addDeviceStore.action.changeDeviceUid,
      payload: updatedDeviceUid
    });
  }

  const selecteDeviceUid = (value) => {
    changeDeviceUid(value);
  }

  const changeDeviceName = (value) => {
    const updatedDeviceName = cloneDeep(addDeviceState.deviceName);
    AddDeviceModel.changeDeviceName(updatedDeviceName, value);

    addDeviceDispatch({
      type: addDeviceStore.action.changeDeviceName,
      payload: updatedDeviceName
    });
  }

  const changeSite = (value) => {
    const updatedSite = cloneDeep(addDeviceState.site);
    AddDeviceModel.changeSite(updatedSite, value);

    addDeviceDispatch({
      type: addDeviceStore.action.changeSite,
      payload: updatedSite
    });
  }

  const changeProfile = (value) => {
    const updatedProfile = cloneDeep(addDeviceState.profile);
    AddDeviceModel.changeProfile(updatedProfile, value);

    addDeviceDispatch({
      type: addDeviceStore.action.changeProfile,
      payload: updatedProfile
    });
  }

  const addInputLicenseField = () => {
    const updatedLicenseKeyList = cloneDeep(addDeviceState.licenseKeyList);
    AddDeviceModel.addLicenseKey(updatedLicenseKeyList);

    addDeviceDispatch({
      type: addDeviceStore.action.changeLicenseKeyList,
      payload: updatedLicenseKeyList
    });
  }

  const removeInputField = (index) => {
    const updatedLicenseKeyList = cloneDeep(addDeviceState.licenseKeyList);
    AddDeviceModel.removeLicenseKey(updatedLicenseKeyList, index);

    addDeviceDispatch({
      type: addDeviceStore.action.changeLicenseKeyList,
      payload: updatedLicenseKeyList
    });
  }

  const changeLicenseKey = (index, value) => {
    const updatedLicenseKeyList = cloneDeep(addDeviceState.licenseKeyList);
    AddDeviceModel.changeLicenseKey(updatedLicenseKeyList, index, value);

    addDeviceDispatch({
      type: addDeviceStore.action.changeLicenseKeyList,
      payload: updatedLicenseKeyList
    });
  }

  const getRemainLicenseKeyHint = () => {
    let totalNumber = addDeviceState.licenseKeyList.list.length;
    let remainNumber = addDeviceState.licenseKeyList.list.length;

    for (const assignedLicense of addDeviceState.licenseKeyList.valueList) {
      for (const originLicense of addDeviceState.licenseKeyList.list) {
        if (assignedLicense.key === originLicense.key) {
          remainNumber--;
        }
      }
    }

    return t(
      'dd26536b8e',
      {
        remaining_number: remainNumber,
        total_number: totalNumber
      }
    );
  }

  const getUnassignLicenseKey = () => {
    const list = [];

    for (const originLicense of addDeviceState.licenseKeyList.list) {
      let unassigned = true;
      for (const assignedLicense of addDeviceState.licenseKeyList.valueList) {
        if (assignedLicense.key === originLicense.key) {
          unassigned = false;
        }
      }

      if (unassigned) {
        list.push(originLicense);
      }
    }

    return list;
  }

  const checkForm = () => {
    if (!addDeviceState?.deviceUid.valid) {
      return false;
    }

    if (!addDeviceState?.deviceName.valid) {
      return false;
    }

    if (!addDeviceState?.profile?.selected) {
      return false;
    }

    let correctLicenseNumber = !addDeviceState?.licenseKeyList?.defaultLicense.key ? 0 : 1;
    let incorrectLicenseNumber = 0;
    for (const licenseKey of addDeviceState?.licenseKeyList?.valueList) {
      if (licenseKey.dirty && licenseKey.valid) {
        correctLicenseNumber++;
      }

      if (licenseKey.dirty && !licenseKey.valid) {
        incorrectLicenseNumber++;
      }
    }

    if (correctLicenseNumber === 0 || incorrectLicenseNumber > 0) {
      return false;
    }

    return true;
  }

  const registerDevice = async () => {
    const reloadBoostrap = () => {
      if (userIdentity === userType.si) {
        dispatch(loadOrgBootstarp(selectedOrg.id));
      } else {
        dispatch(loadGuiBootstarp());
      }
    }

    dispatch(uiActions.showModalSpinner());

    try {
      const response = await controller.registerDevice(selectedOrg.id, addDeviceState);

      if (response.error) {
        throw response.error;
      }

      reloadBoostrap();
      dispatch(uiActions.hideModalSpinner());
      resetMessages();
      changeModalStatus(modalStatus.addDevice.type, false);
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideModalSpinner());
    }
  }

  useEffect(() => {
    modalStatus.addDevice.status && init();
  }, [selectedOrg, modalStatus]);

  useEffect(() => {
    const getDeviceModel = async () => {
      dispatch(uiActions.showModalSpinner());

      // It's unnecessary to handle error situation because however how request it is, server still respond 200
      const response = await controller.getDeviceModel(selectedOrg.id, addDeviceState.deviceUid.value);
      const { modelName, deviceName, defaultLicense, restrictCountryCode } = controller.parseRawDeviceModel(
        response.data,
        addDeviceState.deviceUid.value,
        addDeviceState.deviceUid.originList
      );

      let unusedActivedLicenseCount = 0;
      let licenseList = [];
      if (userIdentity === userType.org) {
        let response = null;

        try {
          response = await controller.getLicenseList(selectedOrg.id, modelName)

          if (response.error) {
            throw response.error;
          }

          response = controller.parseRawLicenseList(response);
          unusedActivedLicenseCount = response.unusedActivedLicenseCount;
          licenseList = response.list;
        } catch (error) {
          setErrorMessage(error.code);
        }
      }

      // If unused actived license count > 0
      setHasUnusedActivedLicense(unusedActivedLicenseCount > 0);

      // Set default values
      const updatedDeviceName = cloneDeep(addDeviceState.deviceName);
      AddDeviceModel.changeDeviceName(updatedDeviceName, deviceName);

      const updatedSite = cloneDeep(addDeviceState.site);
      AddDeviceModel.initSite(updatedSite, restrictCountryCode);
      restrictCountryCode && updatedSite.list.length === 0 && setErrorMessage(1012);

      const updatedProfile = cloneDeep(addDeviceState.profile);
      AddDeviceModel.initProfile(updatedProfile, modelName);

      const updatedLicenseKeyList = cloneDeep(addDeviceState.licenseKeyList);
      AddDeviceModel.initLicenseKeyList(updatedLicenseKeyList, defaultLicense, licenseList);

      addDeviceDispatch({
        type: addDeviceStore.action.setDefaultState,
        payload: {
          deviceName: updatedDeviceName,
          site: updatedSite,
          profile: updatedProfile,
          licenseKeyList: updatedLicenseKeyList
        }
      });

      dispatch(uiActions.hideModalSpinner());
    }

    addDeviceState.deviceUid.valid && getDeviceModel();
  }, [addDeviceState.deviceUid.valid, addDeviceState.deviceUid.value, selectedOrg.id]);

  return (
    <ModalContainer
      openModal={modalStatus.addDevice.status}
      modalWidthType='modal-500px'
      closeModal={() => changeModalStatus(modalStatus.addDevice.type, false)}
      children={
        <>
          {showModalSpinner && <SpinnerForModal />}
          <div className='header'>
            <div className='title'>{t('5290e89436')}</div>
          </div>
          <div className='body add-device-modal-container'>
            {
              addDeviceState.site.restrictCountryCode && addDeviceState.site.list.length === 0 &&
              (
                <MessageBoxGroup
                  messages={messages}
                  ErrorMessagesType={messagesType.error.client}
                  onClose={() => resetMessages(messagesType.error.type)}
                />
              )
            }
            {
              !addDeviceState.site.restrictCountryCode &&
              (
                <MessageBoxGroup
                  messages={messages}
                  ErrorMessagesType={messagesType.error.server}
                  onClose={() => resetMessages(messagesType.error.type)}
                />
              )
            }
            <Row>
              <Col>
                <div className='mt-3'>
                  {/* Device UID */}
                  <div className='modal-form-title required'>{t('e7d22294bd')}</div>
                  <Input
                    type='text'
                    placeholder='Device UID'
                    dataBsToggleOnInput={true}
                    dropdownMenuStyle={{ minWidth: '100%' }}
                    isInvalid={addDeviceState?.deviceUid?.dirty && !addDeviceState?.deviceUid?.valid}
                    value={addDeviceState?.deviceUid?.value}
                    onChange={e => changeDeviceUid(e.target.value)}
                    onClick={resetForm}
                    autoComplete='new-password'
                  >
                    {
                      addDeviceState?.deviceUid?.list?.map((device, index) => {
                        return (
                          <li
                            key={device.uid + '-' + index}
                            onClick={() => selecteDeviceUid(device.uid)}
                            autoComplete='new-password'
                          >
                            {device.uid}
                          </li>
                        );
                      })
                    }
                  </Input>
                </div>
              </Col>
              <Col>
                <div className='mt-3'>
                  {/* Device name */}
                  <div className='modal-form-title'>{t('92394ac496')}</div>
                  <Input
                    type='text'
                    value={addDeviceState?.deviceName?.value}
                    placeholder={'9fa545e2cd'}
                    isInvalid={addDeviceState?.deviceName?.dirty && !addDeviceState?.deviceName?.valid}
                    onChange={e => changeDeviceName(e.target.value)}
                    disabled={!addDeviceState.deviceUid.valid}
                  />
                </div>
              </Col>
            </Row>

            <div>
              {/* Please enter UID to register the device */}
              <div style={{ color: '#797979' }}>{t('d0eb1b0e7b')}</div>
              {/* UID format: XXXX-XXXX-XXXX or XXXXXXXXXXXX */}
              <div style={{ color: '#797979' }}>{t('081e1774f4')}</div>
            </div>

            <Row className='mt-3'>
              <Col>
                {/* Site */}
                <div className='d-inline-flex mb-1'>
                  <div className='modal-form-title required mb-0'>{t('a7d6475ec8')}</div>
                  <TooltipDialog
                    className='ms-1'
                    placement='right'
                    title={t('982dd53466')}
                  />
                </div>
                <DropdownWithItem
                  id='site-dropdown'
                  type='normal'
                  isTruncate
                  style={{ width: '218px' }}
                  selectedItem={addDeviceState?.site?.selected}
                  itemList={addDeviceState?.site?.list}
                  onClick={changeSite}
                  disabled={!addDeviceState.deviceUid.valid}
                />
              </Col>

              <Col>
                {/* Profile */}
                <div className='modal-form-title required'>{t('cce99c598c')}</div>
                <DropdownWithItem
                  id='profile-dropdown'
                  type='normal'
                  isTruncate
                  style={{ width: '218px' }}
                  selectedItem={addDeviceState?.profile?.selected}
                  itemList={addDeviceState?.profile?.list}
                  onClick={changeProfile}
                  disabled={!addDeviceState.deviceUid.valid}
                />
              </Col>
            </Row>

            {addDeviceState?.licenseKeyList?.defaultLicense?.key && (
              <Row className='mt-3'>
                <Col>
                  {/* Complimentary license claimed */}
                  <div className='modal-form-title' >{t('2ce2734029')}</div>
                  <div>
                    <span>{addDeviceState.licenseKeyList.defaultLicense.period}</span>
                    <span className='ms-1'>{t(addDeviceState.licenseKeyList.defaultLicense.unitLabel)}</span>
                  </div>
                </Col>
              </Row>
            )}

            {/* License key */}
            <Row className='mt-3 mb-1'>
              {
                addDeviceState?.licenseKeyList?.valueList?.map((assignedlicenseKey, index) => {
                  return (
                    <Col sm={6} key={'add-device-add-LicenseKey-list-' + index}>
                      {/* License key # xxx */}
                      <div className='modal-form-title required'>{t('5d5c4ad5db')} # {index + 1}</div>
                      <div className='add-license-input-group'>
                        <Input
                          type='text'
                          placeholder={t('64a77f88d7')}
                          value={assignedlicenseKey.key}
                          dataBsToggleOnInput={true}
                          isInvalid={assignedlicenseKey.dirty && !assignedlicenseKey.valid}
                          onChange={e => changeLicenseKey(index, e.target.value)}
                        >
                          {
                            userIdentity === userType.org &&
                            (
                              <>
                                <div className='more-content'>
                                  <div className='show-more-info'>
                                    {getRemainLicenseKeyHint()}
                                  </div>
                                </div>
                                {
                                  getUnassignLicenseKey()?.map((unassignedLicense, subIndex) => {
                                    return (
                                      <li
                                        key={unassignedLicense.key + '-' + subIndex}
                                        onClick={() => changeLicenseKey(index, unassignedLicense.key)}
                                      >
                                        {unassignedLicense.key} ({unassignedLicense.remainDayCount} {t(unassignedLicense.remainDayLabel)})
                                      </li>
                                    );
                                  })
                                }
                              </>
                            )
                          }
                        </Input>
                        <Icon
                          iconTitle={t('f2a6c498fb')}
                          className='icon-close'
                          onClick={e => removeInputField(index)}
                        />
                      </div>
                    </Col>
                  )
                })
              }
            </Row>

            {/* You still have inactive licenses xxxxxx */}
            {hasUnusedActivedLicense && addDeviceState?.licenseKeyList?.valueList?.length === 0 && (
              <Row className='mt-3'>
                <div style={{ color: '#f59d24' }}>{t('36f3a13b4f')}</div>
              </Row>
            )}

            {/* Add more licenses */}
            <ButtonWithIcon
              label={t('c0b06bc507')}
              className='d-flex justify-content-center mt-3'
              iconClassName='icon-expand'
              disabled={!addDeviceState?.deviceUid?.valid}
              onClick={addInputLicenseField}
            />
          </div>

          <div className='footer'>
            <Button
              label={t('ea4788705e')}
              className='btn-cancel'
              onClick={() => changeModalStatus(modalStatus.addDevice.type, false)}
            />
            <Button
              label={t('c9cc8cce24')}
              className='btn-submit'
              disabled={!checkForm()}
              onClick={() => registerDevice()}
            />
          </div>
        </>
      }
    />
  );
};

export default memo(AddDeviceModal);
